<template>
  <div class="content">
    <v-toolbar flat>
      <v-app-bar-nav-icon><v-icon>mdi-office-building</v-icon></v-app-bar-nav-icon>
      <v-toolbar-title>
        {{ federation.name }}
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <div v-if="canManageCurrentFederation">
        <!-- Open edit federation dialog button -->
        <v-btn icon fab @click="openEditFederationDialog(federation)">
          <v-icon>edit</v-icon>
        </v-btn>
      </div>
    </v-toolbar>

    <!-- Edit federation dialog -->
    <v-dialog v-model="editFederationDialog" max-width="500px">
      <v-card>
        <v-card-text>
          <v-form ref="federationForm">
            <v-container>
              <v-row>
                <v-col cols="12" sm="6" md="12">
                  <v-text-field
                    v-model="editedFederation.name"
                    label="Nom de la Fédération"
                    :rules="[rules.required, rules.counter]"
                    counter
                    maxlength="60"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="closeFederationDialog">Annuler</v-btn>
          <v-btn color="blue darken-1" text @click="submitForm()">Modifier</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-tabs v-model="tab">
      <v-tab to="users">Gestion des utilisateurs Fédéraux</v-tab>
      <v-tab to="disciplines">Gestion des disciplines</v-tab>
      <v-tab to="draggables">Gestion des draggables</v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab" v-if="isFederationLoaded" style="overflow: visible; display: block">
      <v-tab-item id="users">
        <federationUsers :federationId_prop="federation.id" />
      </v-tab-item>

      <v-tab-item id="disciplines">
        <disciplines :federationId_prop="federation.id" />
      </v-tab-item>

      <v-tab-item id="draggables">
        <draggables :federationId_prop="federation.id" />
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import { VTabs } from "vuetify/lib";
import Draggables from "./draggables.vue";
import disciplines from "./disciplines";
import federationUsers from "./federationUsers";
import { getFederationByIdAsync } from "@/api/federationsApi";
import { updateFederationAsync, getIsUserPartOfFederationAsync } from "../../api/federationsApi";
import _ from "lodash";
import store from "@/store";
import AuthService from "@/services/AuthService";
import PermissionsService from "@/services/PermissionsService";

export default {
  components: {
    VTabs,
    disciplines,
    federationUsers,
    Draggables,
  },

  props: {
    federationId_prop: [Number, String],
    tab: String,
  },

  watch: {
    $route() {
      this.initPage();
    },
  },

  data: function () {
    return {
      authService: AuthService,
      federation: {},
      isCurrentUserPartOfFederation: false,
      isFederationLoaded: false,
      editFederationDialog: false,
      editedFederation: {},
      federationId: null,
      rules: {
        required: (value) => !!value || "Requis.",
        counter: (value) => (value ? value.length <= 60 : "Max 60 caractères"),
      },
    };
  },

  computed: {
    canViewCurrentFederation() {
      // Can view own federation and is part of current federation
      let canViewOwnFederationAndIsPartOfCurrentFederation = PermissionsService.FederationsPermissionsService.canViewOwnFederation && this.isCurrentUserPartOfFederation;
      // Can view all federations
      let canViewAllFederations = PermissionsService.FederationsPermissionsService.canViewAllFederations;
      return canViewOwnFederationAndIsPartOfCurrentFederation || canViewAllFederations;
    },
    canManageCurrentFederation() {
      // Can manage own federation and is part of current federation
      let canManageCurrentFederationAndIsPartToCurrentFederation =
        PermissionsService.FederationsPermissionsService.canManageOwnFederation && this.isCurrentUserPartOfFederation;
      // Can manage all federations
      let canManageAllFederations = PermissionsService.FederationsPermissionsService.canManageAllFederations;
      return canManageCurrentFederationAndIsPartToCurrentFederation || canManageAllFederations;
    },
  },

  async mounted() {
    await this.initPage();
  },

  methods: {
    async initPage() {
      // Override the props values from the url if provided
      this.federationId = this.$route.params.federationId ? parseInt(this.$route.params.federationId) : this.federationId_prop;

      // Check if the current user is part of current federation
      this.isCurrentUserPartOfFederation = await getIsUserPartOfFederationAsync(this.authService.userId, this.federationId);

      // Check if the user can see this page
      if (!this.canViewCurrentFederation) {
        store.dispatch("setSnackbar", {
          title: `Non Autorisé`,
          text: `Vous n'êtes pas autorisé à consulter cette ressource.`,
          status: "warning",
          timeout: 2000,
        });

        this.$router.replace("/");
      }

      this.getData();
    },

    async getData() {
      this.federation = await getFederationByIdAsync(this.federationId);
      this.isFederationLoaded = true;
    },

    // Open edit federation dialog, and clone current federation to edit
    openEditFederationDialog(federation) {
      this.editedFederation = _.cloneDeep(federation);
      this.editFederationDialog = true;
    },

    // Close edit federation dialog and clean cloned federation
    closeFederationDialog() {
      this.editedFederation = {};
      this.editFederationDialog = false;
    },

    // UPDATE a federation
    async submitForm() {
      // Validate the form
      var isFormValid = this.$refs.federationForm.validate();

      // If the form is valid
      if (isFormValid) {
        var result = await updateFederationAsync(this.editedFederation.id, this.editedFederation);

        // If we have an id > 0 in the result, the save succeeded
        if (result && result.id > 0) {
          store.dispatch("setSnackbar", {
            title: `Fédération mise à jour`,
            text: `La fédération a bien été mise à jour.`,
            status: "success",
            timeout: 2000,
          });

          this.editedFederation = {};
          this.$refs.federationForm.reset();
        }

        this.closeFederationDialog();

        await this.getData();
      }
      // Else display the error messages
      else {
        // Handle missing name
        if (!this.editedFederation.name) {
          store.dispatch("setSnackbar", {
            title: "Titre requis",
            text: "Veuillez renseigner le Titre.",
            status: "error",
            timeout: 2000,
          });
        }
      }
    },
  },
};
</script>
